import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    IonModal,
    IonHeader,
    IonToolbar,
    IonButton,
    IonList,
    IonItem,
    IonLabel,
    IonIcon,
    IonInput,
    IonText,
    IonAlert,
    IonToast,
    IonGrid,
    IonRow,
    IonCol,
    IonCard,
    IonCardContent,
    IonCheckbox,
} from '@ionic/react';
import { checkmark, trashOutline, createOutline, searchOutline, colorWandOutline, checkmarkCircle, alertCircle } from 'ionicons/icons';
import { HexColorPicker } from 'react-colorful';
import { Label } from '@rsmaids/shared/types/contacts';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import styles from './LabelModals.module.scss';
import { useTranslation } from 'react-i18next';
import { useSheetScrollGuard } from '../../hooks/useSheetScrollGuard';

// Predefined colors for the color picker
const PREDEFINED_COLORS = [
    '#3880ff', // blue
    '#3dc2ff', // cyan
    '#5260ff', // indigo
    '#2dd36f', // green
    '#ffc409', // yellow
    '#eb445a', // red
    '#92949c', // medium
    '#222428', // dark
    '#6a64ff', // purple
    '#ed576b', // pink
    '#ff9f43', // orange
    '#f4f5f8', // light
];

const DEFAULT_COLOR = '#3880ff';

// ── Colour maths ──────────────────────────────────────────────────────────
const clamp255 = (n: number) => Math.min(255, Math.max(0, Math.round(n)));

const hexToRgb = (hex: string): [number, number, number] => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
    if (!m) return [56, 128, 255];
    return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
};

const rgbToHex = (r: number, g: number, b: number) =>
    '#' + [r, g, b].map((v) => clamp255(v).toString(16).padStart(2, '0')).join('');

const isHex = (s: string) => /^#?[0-9a-f]{6}$/i.test(s.trim());

interface EyeDropperCtor {
    new (): { open: () => Promise<{ sRGBHex: string }> };
}

// ── In-DOM colour picker ──────────────────────────────────────────────────
// react-colorful's HexColorPicker (saturation area + hue bar) plus hex / R,G,B
// fields and an eyedropper. Rendered inside the dropdown so it never opens an
// uncontrollable native popup.
const InlineColorPicker: React.FC<{
    value: string;
    onChange: (hex: string) => void;
}> = ({ value, onChange }) => {
  const { t: tr } = useTranslation();
    const [r, g, b] = hexToRgb(value);
    const [hexDraft, setHexDraft] = useState(value.toUpperCase());
    // Sync the draft whenever the controlled value changes — computed
    // during render (React's "adjust state when a prop changes" pattern)
    // rather than as a setState call inside an effect.
    const [prevValue, setPrevValue] = useState(value);
    if (value !== prevValue) {
        setPrevValue(value);
        setHexDraft(value.toUpperCase());
    }

    const commitHex = (raw: string) => {
        if (isHex(raw)) onChange(('#' + raw.trim().replace(/^#/, '')).toLowerCase());
        else setHexDraft(value.toUpperCase());
    };

    const setChannel = (idx: 0 | 1 | 2, raw: string) => {
        const n = clamp255(parseInt(raw || '0', 10) || 0);
        const rgb: [number, number, number] = [r, g, b];
        rgb[idx] = n;
        onChange(rgbToHex(...rgb));
    };

    const EyeDropper = (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper;
    const pickWithEyedropper = async () => {
        if (!EyeDropper) return;
        try {
            const res = await new EyeDropper().open();
            if (res?.sRGBHex && isHex(res.sRGBHex)) onChange(res.sRGBHex.toLowerCase());
        } catch {
            /* user cancelled */
        }
    };

    return (
        <IonGrid className={styles.pickerGrid}>
            <IonRow>
                <IonCol size="12">
                    <HexColorPicker color={value} onChange={onChange} className={styles.rcPicker} />
                </IonCol>
            </IonRow>
            <IonRow className={styles.hexRow}>
                <IonCol size="12">
                    <IonText
                        className={styles.hexSwatch}
                        style={{ '--swatch-color': value } as React.CSSProperties}
                    />
                    <IonInput
                        className={styles.hexInput}
                        value={hexDraft}
                        maxlength={7}
                        placeholder="#RRGGBB"
                        onIonInput={(e) => setHexDraft(e.detail.value || '')}
                        onIonBlur={() => commitHex(hexDraft)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commitHex(hexDraft);
                        }}
                    />
                    {EyeDropper && (
                        <IonButton
                            fill="clear"
                            size="small"
                            className={styles.eyedropperButton}
                            onClick={pickWithEyedropper}
                            aria-label={tr('components.LabelsComponents.LabelModals.pickColourFromScreen')}
                        >
                            <IonIcon icon={colorWandOutline} slot="icon-only" />
                        </IonButton>
                    )}
                </IonCol>
            </IonRow>
            <IonRow className={styles.rgbRow}>
                {(['R', 'G', 'B'] as const).map((ch, idx) => (
                    <IonCol size="4" key={ch}>
                        <IonText className={styles.rgbLabel}>{ch}</IonText>
                        <IonInput
                            className={styles.rgbInput}
                            type="number"
                            min={0}
                            max={255}
                            value={[r, g, b][idx]}
                            onIonInput={(e) => setChannel(idx as 0 | 1 | 2, e.detail.value || '0')}
                        />
                    </IonCol>
                ))}
            </IonRow>
        </IonGrid>
    );
};

// ── Anchored color-swatch dropdown ─────────────────────────────────────────
// Portaled to document.body (NOT an in-modal container — the modal clips
// overflow) and positioned against the tapped swatch, flipping above it when
// there isn't room below. Glass styling copied from Contact's client-history
// portal dropdown (.customDropdownList).
const ColorPickerDropdown: React.FC<{
    isOpen: boolean;
    anchorRef: React.RefObject<HTMLElement | null>;
    currentColor: string;
    onPick: (color: string) => void;
    onClose: () => void;
}> = ({ isOpen, anchorRef, currentColor, onPick, onClose }) => {
  const { t: tr } = useTranslation();
    const [customColor, setCustomColor] = useState(currentColor || DEFAULT_COLOR);
    const cardRef = useRef<HTMLIonCardElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    // Sync the draft color whenever open+currentColor change, and clear the
    // computed position on close — computed during render (React's "adjust
    // state when a prop changes" pattern) rather than as setState calls
    // inside effects. The position-computation effect below keeps its own
    // early return but no longer needs to reset `pos` itself.
    const [prevColorSync, setPrevColorSync] = useState<{ isOpen: boolean; currentColor: string }>({ isOpen, currentColor });
    if (prevColorSync.isOpen !== isOpen || prevColorSync.currentColor !== currentColor) {
        setPrevColorSync({ isOpen, currentColor });
        if (isOpen) setCustomColor(currentColor || DEFAULT_COLOR);
    }
    const [wasOpen, setWasOpen] = useState(isOpen);
    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);
        if (!isOpen) setPos(null);
    }

    useLayoutEffect(() => {
        if (!isOpen) return;

        const compute = () => {
            const anchor = anchorRef.current;
            const card = cardRef.current;
            if (!anchor) return;
            const a = anchor.getBoundingClientRect();
            const cardH = card?.getBoundingClientRect().height ?? 0;
            const cardW = card?.getBoundingClientRect().width ?? 232;
            const gap = 6;
            const margin = 8;

            const spaceBelow = window.innerHeight - a.bottom;
            const spaceAbove = a.top;
            const flipUp = cardH > 0 && spaceBelow < cardH + gap + margin && spaceAbove > spaceBelow;

            let top = flipUp ? a.top - cardH - gap : a.bottom + gap;
            top = Math.max(margin, Math.min(top, window.innerHeight - cardH - margin));

            let left = a.left;
            left = Math.max(margin, Math.min(left, window.innerWidth - cardW - margin));

            setPos({ top, left });
        };

        compute();
        // Recompute once the card's real size is known.
        const raf = requestAnimationFrame(compute);

        const onScroll = () => compute();
        const onResize = () => compute();
        const onPointerDown = (e: PointerEvent) => {
            const t = e.target as Node;
            if (anchorRef.current?.contains(t)) return;
            if (cardRef.current?.contains(t)) return;
            onClose();
        };
        document.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        document.addEventListener('pointerdown', onPointerDown);
        return () => {
            cancelAnimationFrame(raf);
            document.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [isOpen, anchorRef, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <IonRow className={styles.pickerPortalRoot}>
            <IonCard
                ref={cardRef}
                className={styles.colorDropdown}
                style={{
                    position: 'absolute',
                    top: pos?.top ?? -9999,
                    left: pos?.left ?? -9999,
                    visibility: pos ? 'visible' : 'hidden',
                }}
            >
                <IonGrid className={styles.colorGrid}>
                    <IonRow className={styles.customColorRow}>
                        <IonCol size="12">
                            <InlineColorPicker value={customColor} onChange={setCustomColor} />
                        </IonCol>
                        <IonCol size="12">
                            <IonButton
                                size="small"
                                className={`${styles.pillButton} ${styles.pillButtonPrimary}`}
                                onClick={() => onPick(customColor)}
                            >{tr('components.LabelsComponents.LabelModals.applyCustom')}
                            </IonButton>
                        </IonCol>
                    </IonRow>
                    <IonRow className={styles.swatchDivider}>
                        {PREDEFINED_COLORS.map((color) => (
                            <IonCol size="3" key={color}>
                                <IonButton
                                    fill="clear"
                                    className={styles.colorSwatchButton}
                                    style={{ '--swatch-color': color } as React.CSSProperties}
                                    onClick={() => onPick(color)}
                                    aria-label={tr('components.LabelsComponents.LabelModals.useColor', { color })}
                                >
                                    {currentColor?.toLowerCase() === color.toLowerCase() && (
                                        <IonIcon icon={checkmark} slot="icon-only" />
                                    )}
                                </IonButton>
                            </IonCol>
                        ))}
                    </IonRow>
                </IonGrid>
            </IonCard>
        </IonRow>,
        document.body,
    );
};

// ── LabelManagementModal ──────────────────────────────────────────────────
export const LabelManagementModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    availableLabels: Label[];
    onLabelsChanged: () => void;
}> = ({ isOpen, onClose, availableLabels, onLabelsChanged }) => {
  const { t: tr } = useTranslation();
  const sheetBodyRef = useSheetScrollGuard();
    const [labels, setLabels] = useState<Label[]>([]);
    const [newLabelName, setNewLabelName] = useState('');
    const [newLabelColor, setNewLabelColor] = useState(DEFAULT_COLOR);
    const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');
    const [showDeleteAlert, setShowDeleteAlert] = useState<string | null>(null);
    const [showToast, setShowToast] = useState<{ message: string; color: string } | null>(null);
    // Color dropdown: 'new' for the create row, otherwise a label id.
    const [colorPickerFor, setColorPickerFor] = useState<string | null>(null);

    const labelService = DataServiceFactory.getService().labels;

    // Ref pointed at the swatch element the dropdown is currently anchored to,
    // set straight from the click event target (always a live DOM node).
    const colorAnchorRef = useRef<HTMLElement | null>(null);

    const toggleColorPicker = (key: string, e: React.MouseEvent<HTMLElement>) => {
        colorAnchorRef.current = e.currentTarget;
        setColorPickerFor((v) => (v === key ? null : key));
    };

    // Reset editable state whenever the modal opens (or the catalog
    // changes while open) — computed during render (React's "adjust state
    // when a prop changes" pattern) rather than as setState calls inside an
    // effect.
    const [prevOpenSync, setPrevOpenSync] = useState<{ isOpen: boolean; availableLabels: typeof availableLabels }>({ isOpen, availableLabels });
    if (isOpen && (prevOpenSync.isOpen !== isOpen || prevOpenSync.availableLabels !== availableLabels)) {
        setPrevOpenSync({ isOpen, availableLabels });
        setLabels(availableLabels);
        setNewLabelName('');
        setNewLabelColor(DEFAULT_COLOR);
        setEditingLabelId(null);
        setColorPickerFor(null);
    } else if (!isOpen && prevOpenSync.isOpen) {
        setPrevOpenSync({ isOpen, availableLabels });
    }

    const clearCache = () => {
        localStorage.removeItem('cached-labels');
        localStorage.removeItem('cached-labels-timestamp');
    };

    const handleCreateLabel = async () => {
        if (!newLabelName.trim()) {
            setShowToast({ message: tr('components.LabelsComponents.LabelModals.labelNameCannotBeEmpty'), color: 'danger' });
            return;
        }
        try {
            const created = await labelService.createLabel(newLabelName.trim(), newLabelColor);
            setLabels((prev) => [...prev, created]);
            setNewLabelName('');
            setNewLabelColor(DEFAULT_COLOR);
            clearCache();
            setShowToast({ message: tr('components.LabelsComponents.LabelModals.labelCreated'), color: 'success' });
            onLabelsChanged();
        } catch (error) {
            console.error('Error creating label:', error);
            setShowToast({ message: tr('components.LabelsComponents.LabelModals.failedToCreateLabel'), color: 'danger' });
        }
    };

    const handleRenameLabel = async (labelId: string) => {
        const label = labels.find((l) => l.id === labelId);
        if (!label) return;
        if (!editingName.trim()) {
            setShowToast({ message: tr('components.LabelsComponents.LabelModals.labelNameCannotBeEmpty'), color: 'danger' });
            return;
        }
        const newName = editingName.trim();
        try {
            const updated = await labelService.updateLabel(labelId, newName, label.color || DEFAULT_COLOR);
            setLabels((prev) => prev.map((l) => (l.id === labelId ? updated : l)));
            setEditingLabelId(null);
            setEditingName('');
            clearCache();
            setShowToast({ message: tr('components.LabelsComponents.LabelModals.labelUpdated'), color: 'success' });
            onLabelsChanged();
        } catch (error) {
            console.error('Error updating label:', error);
            setShowToast({ message: tr('components.LabelsComponents.LabelModals.failedToUpdateLabel'), color: 'danger' });
        }
    };

    const handleChangeColor = async (color: string) => {
        const target = colorPickerFor;
        setColorPickerFor(null);
        if (!target) return;
        if (target === 'new') {
            setNewLabelColor(color);
            return;
        }
        const label = labels.find((l) => l.id === target);
        if (!label) return;
        try {
            const updated = await labelService.updateLabel(label.id, label.name, color);
            setLabels((prev) => prev.map((l) => (l.id === label.id ? updated : l)));
            clearCache();
            setShowToast({ message: tr('components.LabelsComponents.LabelModals.labelColorUpdated'), color: 'success' });
            onLabelsChanged();
        } catch (error) {
            console.error('Error updating label color:', error);
            setShowToast({ message: tr('components.LabelsComponents.LabelModals.failedToUpdateColor'), color: 'danger' });
        }
    };

    const handleDeleteLabel = async (labelId: string) => {
        try {
            const success = await labelService.deleteLabel(labelId);
            if (success) {
                setLabels((prev) => prev.filter((l) => l.id !== labelId));
                clearCache();
                setShowToast({ message: tr('components.LabelsComponents.LabelModals.labelDeleted'), color: 'success' });
                onLabelsChanged();
            } else {
                setShowToast({ message: tr('components.LabelsComponents.LabelModals.failedToDeleteLabel'), color: 'danger' });
            }
        } catch (error) {
            console.error('Error deleting label:', error);
            setShowToast({ message: tr('components.LabelsComponents.LabelModals.failedToDeleteLabel'), color: 'danger' });
        }
    };

    const startEdit = (label: Label) => {
        setEditingLabelId(label.id);
        setEditingName(label.name);
    };

    const activeColor =
        colorPickerFor === 'new'
            ? newLabelColor
            : labels.find((l) => l.id === colorPickerFor)?.color || DEFAULT_COLOR;

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            breakpoints={[0, 1]}
            initialBreakpoint={1}
            backdropDismiss={true}
            className={styles.glassModal}
            mode="ios"
        >
            <IonHeader className={styles.glassHeader}>
                <IonToolbar>
                    <IonItem lines="none" className={styles.addRow}>
                        <IonText
                            slot="start"
                            role="button"
                            tabIndex={0}
                            className={styles.swatch}
                            style={{ '--swatch-color': newLabelColor } as React.CSSProperties}
                            onClick={(e) => toggleColorPicker('new', e)}
                            aria-label={tr('components.LabelsComponents.LabelModals.chooseNewLabelColor')}
                        />
                        <IonInput
                            value={newLabelName}
                            onIonInput={(e) => setNewLabelName(e.detail.value || '')}
                            placeholder={tr('components.LabelsComponents.LabelModals.enterLabelName')}
                            clearInput
                        />
                        <IonButton
                            slot="end"
                            className={`${styles.headerPill} ${styles.headerPillPrimary}`}
                            onClick={handleCreateLabel}
                            disabled={!newLabelName.trim()}
                        >{tr('components.LabelsComponents.LabelModals.add')}
                        </IonButton>
                    </IonItem>
                </IonToolbar>
            </IonHeader>

            <IonCardContent ref={sheetBodyRef} className={styles.glassContent}>
                <IonList className={styles.scrollList}>
                    {labels.map((label) => (
                        <IonItem key={label.id} lines="none" className={styles.row} detail={false}>
                            {editingLabelId === label.id ? (
                                <>
                                    <IonInput
                                        value={editingName}
                                        onIonInput={(e) => setEditingName(e.detail.value || '')}
                                        placeholder={tr('components.LabelsComponents.LabelModals.enterFieldName')}
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleRenameLabel(label.id);
                                            if (e.key === 'Escape') setEditingLabelId(null);
                                        }}
                                    />
                                    <IonButton fill="clear" slot="end" onClick={() => handleRenameLabel(label.id)}>{tr('components.LabelsComponents.LabelModals.save')}
                                    </IonButton>
                                    <IonButton fill="clear" slot="end" color="medium" onClick={() => setEditingLabelId(null)}>{tr('components.LabelsComponents.LabelModals.cancel')}
                                    </IonButton>
                                </>
                            ) : (
                                <>
                                    <IonText
                                        slot="start"
                                        role="button"
                                        tabIndex={0}
                                        className={styles.swatch}
                                        style={{ '--swatch-color': label.color || DEFAULT_COLOR } as React.CSSProperties}
                                        onClick={(e) => toggleColorPicker(label.id, e)}
                                        aria-label={tr('components.LabelsComponents.LabelModals.changeColorFor', { name: label.name })}
                                    />
                                    <IonLabel onClick={() => startEdit(label)}>{label.name}</IonLabel>
                                    <IonButton
                                        slot="end"
                                        fill="clear"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            startEdit(label);
                                        }}
                                    >
                                        <IonIcon icon={createOutline} slot="icon-only" />
                                    </IonButton>
                                    <IonButton
                                        slot="end"
                                        fill="clear"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowDeleteAlert(label.id);
                                        }}
                                    >
                                        <IonIcon icon={trashOutline} slot="icon-only" color="danger" />
                                    </IonButton>
                                </>
                            )}
                        </IonItem>
                    ))}

                    {labels.length === 0 && (
                        <IonItem lines="none" className={styles.row}>
                            <IonLabel className="ion-text-center">
                                <IonText color="medium">{tr('components.LabelsComponents.LabelModals.noLabelsYet')}</IonText>
                            </IonLabel>
                        </IonItem>
                    )}
                </IonList>

                <ColorPickerDropdown
                    isOpen={colorPickerFor !== null}
                    anchorRef={colorAnchorRef}
                    currentColor={activeColor}
                    onPick={handleChangeColor}
                    onClose={() => setColorPickerFor(null)}
                />
            </IonCardContent>

            <IonAlert
                isOpen={!!showDeleteAlert}
                onDidDismiss={() => setShowDeleteAlert(null)}
                header={tr('components.LabelsComponents.LabelModals.deleteLabel')}
                message={tr('components.LabelsComponents.LabelModals.areYouSureYouWantTo')}
                buttons={[
                    { text: tr('components.LabelsComponents.LabelModals.cancel'), role: 'cancel' },
                    {
                        text: tr('components.LabelsComponents.LabelModals.delete'),
                        role: 'destructive',
                        handler: () => {
                            if (showDeleteAlert) handleDeleteLabel(showDeleteAlert);
                        },
                    },
                ]}
            />

            <IonToast
                isOpen={!!showToast}
                onDidDismiss={() => setShowToast(null)}
                message={showToast?.message}
                icon={showToast?.color === 'danger' ? alertCircle : checkmarkCircle}
                duration={2000}
                position="bottom"
                className={`${styles.glassToast} ${showToast?.color === 'danger' ? styles.glassToastDanger : styles.glassToastSuccess}`}
            />
        </IonModal>
    );
};

// ── LabelSelectionModal ───────────────────────────────────────────────────
export const LabelSelectionModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSave: (selectedLabelIds: string[]) => void;
    availableLabels: Label[];
    currentLabelIds: string[];
}> = ({ isOpen, onClose, onSave, availableLabels, currentLabelIds }) => {
  const { t: tr } = useTranslation();
  const sheetBodyRef = useSheetScrollGuard();
    const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
    const [searchText, setSearchText] = useState('');
    const [showManageLabels, setShowManageLabels] = useState(false);
    const [localLabels, setLocalLabels] = useState<Label[]>(availableLabels);

    const labelService = DataServiceFactory.getService().labels;

    // Reset editable state whenever the modal opens (or its source data
    // changes while open) — computed during render (React's "adjust state
    // when a prop changes" pattern) rather than as setState calls inside an
    // effect.
    const [prevSelectSync, setPrevSelectSync] = useState<{ isOpen: boolean; currentLabelIds: string[]; availableLabels: Label[] }>({ isOpen, currentLabelIds, availableLabels });
    if (isOpen && (prevSelectSync.isOpen !== isOpen || prevSelectSync.currentLabelIds !== currentLabelIds || prevSelectSync.availableLabels !== availableLabels)) {
        setPrevSelectSync({ isOpen, currentLabelIds, availableLabels });
        setSelectedLabelIds([...currentLabelIds]);
        setLocalLabels(availableLabels);
        setSearchText('');
    } else if (!isOpen && prevSelectSync.isOpen) {
        setPrevSelectSync({ isOpen, currentLabelIds, availableLabels });
    }

    const toggleLabel = (labelId: string) => {
        setSelectedLabelIds((prev) =>
            prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
        );
    };

    const handleSave = () => {
        onSave(selectedLabelIds);
        onClose();
    };

    const handleLabelsChanged = async () => {
        try {
            const refreshed = await labelService.getAllLabels();
            setLocalLabels(refreshed);
        } catch (error) {
            console.error('Error refreshing labels:', error);
        }
    };

    const filteredLabels = localLabels.filter((label) =>
        label.name.toLowerCase().includes(searchText.toLowerCase())
    );

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            breakpoints={[0, 1]}
            initialBreakpoint={1}
            backdropDismiss={true}
            className={styles.glassModal}
            mode="ios"
        >
            <IonHeader className={styles.glassHeader}>
                <IonToolbar>
                    <IonItem lines="none" className={styles.addRow}>
                        <IonIcon icon={searchOutline} slot="start" className={styles.searchIcon} />
                        <IonInput
                            value={searchText}
                            onIonInput={(e) => setSearchText(e.detail.value || '')}
                            placeholder={tr('components.LabelsComponents.LabelModals.searchLabels')}
                            clearInput
                            className={styles.searchInput}
                        />
                        <IonButton
                            slot="end"
                            className={`${styles.headerPill} ${styles.headerPillSecondary}`}
                            onClick={() => setShowManageLabels(true)}
                        >{tr('components.LabelsComponents.LabelModals.manage')}
                        </IonButton>
                        <IonButton
                            slot="end"
                            className={`${styles.headerPill} ${styles.headerPillPrimary}`}
                            onClick={handleSave}
                        >{tr('components.LabelsComponents.LabelModals.save')}
                        </IonButton>
                    </IonItem>
                </IonToolbar>
            </IonHeader>

            <IonCardContent ref={sheetBodyRef} className={styles.glassContent}>
                <IonList className={styles.scrollList}>
                    {filteredLabels.map((label) => {
                        const checked = selectedLabelIds.includes(label.id);
                        return (
                            <IonItem
                                key={label.id}
                                button
                                detail={false}
                                lines="none"
                                className={`${styles.row} ${checked ? styles.rowSelected : ''}`}
                                onClick={() => toggleLabel(label.id)}
                            >
                                <IonText
                                    slot="start"
                                    className={styles.swatch}
                                    style={{ '--swatch-color': label.color || DEFAULT_COLOR } as React.CSSProperties}
                                />
                                <IonLabel>{label.name}</IonLabel>
                                <IonCheckbox
                                    slot="end"
                                    className={styles.rowCheckbox}
                                    checked={checked}
                                    onIonChange={() => toggleLabel(label.id)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </IonItem>
                        );
                    })}

                    {filteredLabels.length === 0 && (
                        <IonItem lines="none" className={styles.row}>
                            <IonLabel className="ion-text-center">
                                <IonText color="medium">{tr('components.LabelsComponents.LabelModals.noLabelsFound')}</IonText>
                            </IonLabel>
                        </IonItem>
                    )}
                </IonList>

                <LabelManagementModal
                    isOpen={showManageLabels}
                    onClose={() => setShowManageLabels(false)}
                    availableLabels={localLabels}
                    onLabelsChanged={handleLabelsChanged}
                />
            </IonCardContent>
        </IonModal>
    );
};
